import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { TrendingUp, TrendingDown, Minus, Building2, Mail, Target, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * HealthIndicator — traffic-light dot + trend arrow.
 */
export const HealthIndicator = ({ health }) => {
  const config = {
    green: { color: 'bg-emerald-500', label: 'Healthy', icon: TrendingUp },
    yellow: { color: 'bg-amber-500', label: 'Moderate', icon: Minus },
    red: { color: 'bg-red-500', label: 'At Risk', icon: TrendingDown },
  };
  const { color, label, icon: Icon } = config[health] || config.yellow;
  return (
    <div className="flex items-center gap-1.5" title={label}>
      <div className={cn('w-2 h-2 rounded-full', color)} />
      <Icon className="w-3 h-3 text-slate-400" />
    </div>
  );
};

/**
 * ScoreBadge — rounded pill with 4 colour tiers based on lead score.
 */
export const ScoreBadge = ({ score, size = 'default' }) => {
  const getScoreColor = (s) => {
    if (s >= 80) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (s >= 60) return 'bg-blue-100 text-blue-700 border-blue-200';
    if (s >= 40) return 'bg-amber-100 text-amber-700 border-amber-200';
    return 'bg-slate-100 text-slate-700 border-slate-200';
  };
  return (
    <span className={cn(
      'inline-flex items-center font-semibold rounded-full border',
      getScoreColor(score),
      size === 'small' ? 'px-1.5 py-0.5 text-xs' : 'px-2 py-1 text-sm',
    )}>
      {Math.round(score)}
    </span>
  );
};

/**
 * LeadCard — draggable card shown inside a PipelineColumn.
 * Rendered many times so keep it lean; heavy modals are lazy-loaded siblings.
 */
export const LeadCard = ({ lead, onClick, isSelected, onSelect }) => {
  const score = lead.calculated_score || lead.score?.total_score || 0;
  const probability = lead.ai_prediction?.close_probability || 0;

  return (
    <div
      className={cn(
        'bg-white rounded-xl border p-4 hover:shadow-lg transition-all cursor-pointer group',
        isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-slate-200 hover:border-blue-200',
      )}
      onClick={() => onClick(lead)}
      data-testid={`lead-card-${lead.lead_id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          {onSelect && (
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => onSelect?.(lead.lead_id)}
              onClick={(e) => e.stopPropagation()}
              className="mr-1"
              data-testid={`lead-checkbox-${lead.lead_id}`}
            />
          )}
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold text-sm">
            {lead.name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div>
            <h4 className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">
              {lead.name}
            </h4>
            {lead.company && (
              <p className="text-xs text-slate-500 flex items-center gap-1">
                <Building2 className="w-3 h-3" />
                {lead.company}
              </p>
            )}
          </div>
        </div>
        <HealthIndicator health={lead.health || 'yellow'} />
      </div>

      <div className="space-y-2">
        {lead.email && (
          <p className="text-xs text-slate-500 flex items-center gap-1 truncate">
            <Mail className="w-3 h-3 flex-shrink-0" />
            {lead.email}
          </p>
        )}
        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
          <div className="flex items-center gap-2">
            <ScoreBadge score={score} size="small" />
            <span className="text-xs text-slate-400">Score</span>
          </div>
          <div className="flex items-center gap-1 text-xs">
            <Target className="w-3 h-3 text-blue-500" />
            <span className="font-medium text-blue-600">{Math.round(probability)}%</span>
          </div>
        </div>
        {lead.ai_prediction?.next_best_action && (
          <div className="bg-blue-50 rounded-lg p-2 mt-2">
            <p className="text-xs text-blue-700 flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              {lead.ai_prediction.next_best_action}
            </p>
          </div>
        )}
      </div>

      {lead.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {lead.tags.slice(0, 2).map((tag, idx) => (
            <Badge key={`tag-${tag}-${idx}`} variant="secondary" className="text-xs px-1.5 py-0">
              {tag}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};
